import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getEmployees, getPayslips, calculatePayslip, createPayslip, downloadPayslipPdf, validatePayslip } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  FileText,
  Plus,
  Calculator,
  Download,
  Check,
  Clock,
  CreditCard,
  User,
  Calendar,
  Euro,
  TrendingDown,
  TrendingUp,
  Eye,
  CheckCircle,
} from 'lucide-react';

const PayslipsPage = () => {
  const { t } = useLanguage();
  const [payslips, setPayslips] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  const [formData, setFormData] = useState({
    employee_id: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    worked_days: 21,
    overtime_hours: 0,
    bonus: 0,
    deduction_other: 0,
    notes: '',
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [payslipsRes, employeesRes] = await Promise.all([
        getPayslips({ month: selectedMonth, year: selectedYear }),
        getEmployees({ active_only: true })
      ]);
      setPayslips(payslipsRes.data || []);
      setEmployees(employeesRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      // Don't show error toast for 404 - just means no payslips yet
      if (error.response?.status !== 404) {
        toast.error(t('hr.loadError') || 'Erreur de chargement');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCalculate = async () => {
    if (!formData.employee_id) {
      toast.error(t('hr.selectEmployee') || 'Veuillez sélectionner un employé');
      return;
    }

    try {
      const response = await calculatePayslip({
        employee_id: formData.employee_id,
        period_month: parseInt(formData.month),
        period_year: parseInt(formData.year),
        worked_days: parseFloat(formData.worked_days) || 21,
        overtime_hours: parseFloat(formData.overtime_hours) || 0,
        bonus: parseFloat(formData.bonus) || 0,
        deduction_other: parseFloat(formData.deduction_other) || 0,
        notes: formData.notes,
      });
      
      setPreviewData(response.data);
      setShowPreview(true);
    } catch (error) {
      console.error('Error calculating payslip:', error);
      toast.error(error.response?.data?.detail || t('hr.calculateError') || 'Erreur de calcul');
    }
  };

  const handleCreatePayslip = async () => {
    try {
      await createPayslip({
        employee_id: formData.employee_id,
        period_month: parseInt(formData.month),
        period_year: parseInt(formData.year),
        worked_days: parseFloat(formData.worked_days) || 21,
        overtime_hours: parseFloat(formData.overtime_hours) || 0,
        bonus: parseFloat(formData.bonus) || 0,
        deduction_other: parseFloat(formData.deduction_other) || 0,
        notes: formData.notes,
      });
      
      toast.success(t('hr.payslipCreated') || 'Fiche de paie créée');
      setShowForm(false);
      setShowPreview(false);
      setPreviewData(null);
      loadData();
    } catch (error) {
      console.error('Error creating payslip:', error);
      toast.error(error.response?.data?.detail || t('hr.createError') || 'Erreur lors de la création');
    }
  };

  const handleValidate = async (id) => {
    try {
      await validatePayslip(id);
      toast.success(t('hr.payslipValidated') || 'Fiche de paie validée');
      loadData();
    } catch (error) {
      console.error('Error validating payslip:', error);
      toast.error(t('hr.validateError') || 'Erreur lors de la validation');
    }
  };

  const handleDownload = async (id) => {
    try {
      const response = await downloadPayslipPdf(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fiche-paie-${id}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(t('hr.downloadError') || 'Erreur lors du téléchargement');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-CH', { 
      style: 'currency', 
      currency: 'CHF',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { color: 'bg-yellow-100 text-yellow-800', label: 'Brouillon', icon: Clock },
      validated: { color: 'bg-green-100 text-green-800', label: 'Validée', icon: Check },
      paid: { color: 'bg-blue-100 text-blue-800', label: 'Payée', icon: CreditCard },
    };
    const { color, label, icon: Icon } = config[status] || config.draft;
    return (
      <Badge className={`${color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  const months = [
    { value: 1, label: 'Janvier' },
    { value: 2, label: 'Février' },
    { value: 3, label: 'Mars' },
    { value: 4, label: 'Avril' },
    { value: 5, label: 'Mai' },
    { value: 6, label: 'Juin' },
    { value: 7, label: 'Juillet' },
    { value: 8, label: 'Août' },
    { value: 9, label: 'Septembre' },
    { value: 10, label: 'Octobre' },
    { value: 11, label: 'Novembre' },
    { value: 12, label: 'Décembre' },
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payslips-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-blue-600" />
            {t('hr.payslips') || 'Fiches de paie'}
          </h1>
          <p className="text-slate-500 mt-1">
            {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
          </p>
        </div>
        <Button 
          onClick={() => setShowForm(true)}
          className="bg-blue-600 hover:bg-blue-700"
          disabled={employees.length === 0}
          data-testid="create-payslip-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('hr.newPayslip') || 'Nouvelle fiche de paie'}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <Select 
                value={selectedMonth.toString()} 
                onValueChange={(v) => setSelectedMonth(parseInt(v))}
              >
                <SelectTrigger className="w-[150px]" data-testid="filter-month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map((m) => (
                    <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Select 
                value={selectedYear.toString()} 
                onValueChange={(v) => setSelectedYear(parseInt(v))}
              >
                <SelectTrigger className="w-[100px]" data-testid="filter-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t('hr.totalGross') || 'Total brut'}</p>
                <p className="text-2xl font-bold text-slate-900">
                  {formatCurrency(payslips.reduce((sum, p) => sum + (p.gross_total || p.calculation?.summary?.total_gross || 0), 0))}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t('hr.totalDeductions') || 'Déductions'}</p>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(payslips.reduce((sum, p) => sum + (p.total_deductions || p.calculation?.summary?.total_employee_deductions || 0), 0))}
                </p>
              </div>
              <TrendingDown className="w-8 h-8 text-red-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t('hr.totalNet') || 'Total net'}</p>
                <p className="text-2xl font-bold text-blue-600">
                  {formatCurrency(payslips.reduce((sum, p) => sum + (p.net_salary || p.calculation?.summary?.net_salary || 0), 0))}
                </p>
              </div>
              <Euro className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t('hr.payslipsCount') || 'Fiches'}</p>
                <p className="text-2xl font-bold text-slate-900">{payslips.length}</p>
              </div>
              <FileText className="w-8 h-8 text-slate-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payslips Table */}
      <Card>
        <CardContent className="p-0">
          {payslips.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {t('hr.noPayslips') || 'Aucune fiche de paie'}
              </h3>
              <p className="text-slate-500 mb-4">
                {t('hr.createFirstPayslip') || 'Créez votre première fiche de paie'}
              </p>
              {employees.length === 0 && (
                <p className="text-amber-600 text-sm">
                  {t('hr.addEmployeesFirst') || 'Ajoutez d\'abord des employés'}
                </p>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('hr.employee') || 'Employé'}</TableHead>
                  <TableHead>{t('hr.period') || 'Période'}</TableHead>
                  <TableHead className="text-right">{t('hr.gross') || 'Brut'}</TableHead>
                  <TableHead className="text-right">{t('hr.deductions') || 'Déductions'}</TableHead>
                  <TableHead className="text-right">{t('hr.net') || 'Net'}</TableHead>
                  <TableHead>{t('hr.status') || 'Statut'}</TableHead>
                  <TableHead className="text-right">{t('common.actions') || 'Actions'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payslips.map((payslip) => {
                  // Extract calculation data - backend flattens summary data to root level
                  const grossTotal = payslip.gross_total || payslip.calculation?.summary?.total_gross || 0;
                  const totalDeductions = payslip.total_deductions || payslip.calculation?.summary?.total_employee_deductions || 0;
                  const netSalary = payslip.net_salary || payslip.calculation?.summary?.net_salary || 0;
                  return (
                  <TableRow key={payslip.id} data-testid={`payslip-row-${payslip.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <User className="w-4 h-4 text-blue-600" />
                        </div>
                        <span className="font-medium">{payslip.employee_name || payslip.calculation?.employee?.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {months.find(m => m.value === payslip.period_month)?.label} {payslip.period_year}
                    </TableCell>
                    <TableCell className="text-right">{formatCurrency(grossTotal)}</TableCell>
                    <TableCell className="text-right text-red-600">
                      -{formatCurrency(totalDeductions)}
                    </TableCell>
                    <TableCell className="text-right font-bold text-blue-600">
                      {formatCurrency(netSalary)}
                    </TableCell>
                    <TableCell>{getStatusBadge(payslip.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {payslip.status === 'draft' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleValidate(payslip.id)}
                            title="Valider"
                            data-testid={`validate-payslip-${payslip.id}`}
                          >
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(payslip.id)}
                          title="Télécharger PDF"
                          data-testid={`download-payslip-${payslip.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Payslip Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-blue-600" />
              {t('hr.newPayslip') || 'Nouvelle fiche de paie'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>{t('hr.employee') || 'Employé'} *</Label>
              <Select 
                value={formData.employee_id} 
                onValueChange={(v) => handleInputChange('employee_id', v)}
              >
                <SelectTrigger data-testid="select-employee">
                  <SelectValue placeholder={t('hr.selectEmployee') || 'Sélectionner un employé'} />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name} - {emp.position}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('hr.month') || 'Mois'}</Label>
                <Select 
                  value={formData.month.toString()} 
                  onValueChange={(v) => handleInputChange('month', parseInt(v))}
                >
                  <SelectTrigger data-testid="select-month">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((m) => (
                      <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('hr.year') || 'Année'}</Label>
                <Select 
                  value={formData.year.toString()} 
                  onValueChange={(v) => handleInputChange('year', parseInt(v))}
                >
                  <SelectTrigger data-testid="select-year">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('hr.workedDays') || 'Jours travaillés'}</Label>
                <Input
                  type="number"
                  min="0"
                  max="31"
                  value={formData.worked_days}
                  onChange={(e) => handleInputChange('worked_days', e.target.value)}
                  data-testid="input-worked-days"
                />
              </div>
              <div>
                <Label>{t('hr.overtimeHours') || 'Heures supp.'}</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.overtime_hours}
                  onChange={(e) => handleInputChange('overtime_hours', e.target.value)}
                  data-testid="input-overtime"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('hr.bonus') || 'Prime (CHF)'}</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.bonus}
                  onChange={(e) => handleInputChange('bonus', e.target.value)}
                  data-testid="input-bonus"
                />
              </div>
              <div>
                <Label>{t('hr.otherDeductions') || 'Autre déduction (CHF)'}</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.deduction_other}
                  onChange={(e) => handleInputChange('deduction_other', e.target.value)}
                  data-testid="input-deduction"
                />
              </div>
            </div>

            <div>
              <Label>{t('hr.notes') || 'Remarques'}</Label>
              <Input
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder={t('hr.notesPlaceholder') || 'Notes optionnelles...'}
                data-testid="input-notes"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowForm(false)}>
                {t('common.cancel') || 'Annuler'}
              </Button>
              <Button 
                onClick={handleCalculate} 
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="calculate-payslip-btn"
              >
                <Calculator className="w-4 h-4 mr-2" />
                {t('hr.calculate') || 'Calculer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-600" />
              {t('hr.payslipPreview') || 'Aperçu de la fiche de paie'}
            </DialogTitle>
          </DialogHeader>
          
          {previewData && (
            <div className="space-y-6">
              {/* Employee Info */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <User className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-bold text-lg">{previewData.employee?.name}</p>
                    <p className="text-slate-500">
                      {months.find(m => m.value === previewData.period?.month)?.label} {previewData.period?.year}
                    </p>
                  </div>
                </div>
              </div>

              {/* Breakdown */}
              <div className="space-y-3">
                <h4 className="font-medium text-slate-900">{t('hr.earnings') || 'Revenus'}</h4>
                <div className="bg-green-50 p-4 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span>{t('hr.baseSalary') || 'Salaire de base'}</span>
                    <span className="font-medium">{formatCurrency(previewData.earnings?.base_salary)}</span>
                  </div>
                  {previewData.earnings?.thirteenth_month > 0 && (
                    <div className="flex justify-between">
                      <span>{t('hr.13thMonth') || '13ème salaire'}</span>
                      <span className="font-medium">{formatCurrency(previewData.earnings?.thirteenth_month)}</span>
                    </div>
                  )}
                  {previewData.allowances?.total > 0 && (
                    <div className="flex justify-between">
                      <span>{t('hr.allowances') || 'Indemnités'}</span>
                      <span className="font-medium">{formatCurrency(previewData.allowances?.total)}</span>
                    </div>
                  )}
                  {previewData.earnings?.overtime > 0 && (
                    <div className="flex justify-between">
                      <span>{t('hr.overtimePay') || 'Heures supplémentaires'}</span>
                      <span className="font-medium">{formatCurrency(previewData.earnings?.overtime)}</span>
                    </div>
                  )}
                  {previewData.earnings?.bonus > 0 && (
                    <div className="flex justify-between">
                      <span>{t('hr.bonus') || 'Prime'}</span>
                      <span className="font-medium">{formatCurrency(previewData.earnings?.bonus)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t pt-2 font-bold">
                    <span>{t('hr.totalGross') || 'Total brut'}</span>
                    <span className="text-green-700">{formatCurrency(previewData.summary?.total_gross)}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-medium text-slate-900">{t('hr.deductions') || 'Déductions'}</h4>
                <div className="bg-red-50 p-4 rounded-lg space-y-2">
                  {previewData.deductions?.map((deduction, index) => (
                    deduction.employee_amount > 0 && (
                      <div key={index} className="flex justify-between">
                        <span>{deduction.name} ({deduction.rate_employee?.toFixed(2)}%)</span>
                        <span className="font-medium text-red-600">-{formatCurrency(deduction.employee_amount)}</span>
                      </div>
                    )
                  ))}
                  {previewData.summary?.advance_deduction > 0 && (
                    <div className="flex justify-between">
                      <span>{t('hr.otherDeductions') || 'Autres déductions'}</span>
                      <span className="font-medium text-red-600">-{formatCurrency(previewData.summary?.advance_deduction)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t pt-2 font-bold">
                    <span>{t('hr.totalDeductions') || 'Total déductions'}</span>
                    <span className="text-red-700">-{formatCurrency(previewData.summary?.total_employee_deductions)}</span>
                  </div>
                </div>
              </div>

              {/* Net */}
              <div className="bg-blue-100 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold">{t('hr.netSalary') || 'Salaire net'}</span>
                  <span className="text-2xl font-bold text-blue-700">{formatCurrency(previewData.summary?.net_salary)}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowPreview(false)}>
                  {t('common.cancel') || 'Annuler'}
                </Button>
                <Button 
                  onClick={handleCreatePayslip} 
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="confirm-payslip-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  {t('hr.createPayslip') || 'Créer la fiche de paie'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayslipsPage;
